import styled from 'styled-components';
import { useSelector } from 'react-redux';

import { selectWysiwigEditorEnabled } from 'core/userContext/selectors';
import { AceEditorTheme } from 'modules/aceEditor/types';
import {
  EmailContentDefinition,
  HtmlEditor,
  InAppContentDefinition,
  MessageMedium,
} from 'modules/graphql/types';
import { FormField } from 'ui/formField';
import { ModalBody } from 'ui/modal';
import { BasicSelect, BasicOption } from 'ui/selects';

import {
  CheckboxField,
  SettingsReadOnlyText,
  SettingsSectionHeader,
  displayContent,
} from 'modules/templateEditor/common';
import { useContentEditorPreferencesContext } from 'modules/templateEditor/context/ContentEditorPreferencesContext';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useTemplateQueryContext } from 'modules/templateEditor/context/TemplateQueryContext';
import {
  getContentEditorOptions,
  getSideBySideEditorThemeOptions,
} from './ContentEditorPreferencesModal.utils';
import { CONTENT_EDITOR_LABEL_MAPPER } from './ContentEditorPreferencesModal.constants';

const StyledModalBody = styled(ModalBody)`
  align-items: stretch;
  background-color: ${({ theme }) => theme.colors.backgroundColors.backgroundSecondary};
  border: none;
  display: flex;
  flex-direction: column;
  height: ${({ theme }) => theme.spacing.utils.pxToRem(453)};
  justify-content: flex-start;
  padding: ${({ theme }) => theme.spacing.values.plusHalf};
`;

export const TabStyling = () => {
  const { contentEditor, initialTemplate, liveTemplate, setCurrentMediumFields } =
    useEditableFieldsContext();
  const { medium } = useTemplateQueryContext();
  const { selectedSideBySideTheme, setSelectedSideBySideTheme } =
    useContentEditorPreferencesContext();
  const emailData = (liveTemplate?.mediumData || {}) as EmailContentDefinition;
  const cleanHtmlMediumData = (initialTemplate?.mediumData || {}) as
    | EmailContentDefinition
    | InAppContentDefinition;
  const wysiwygEditorEnabled =
    useSelector(selectWysiwigEditorEnabled) || medium === MessageMedium.InApp;

  return (
    <StyledModalBody>
      <SettingsSectionHeader>{displayContent.headerPreferences}</SettingsSectionHeader>
      {medium === MessageMedium.Email && (
        <FormField labelText={displayContent.labelConvertCSS} isHorizontalLayout>
          <CheckboxField
            label={displayContent.strConvertCSS}
            status={!!emailData.inlineCss}
            onClick={() => {
              setCurrentMediumFields({ inlineCss: !emailData.inlineCss });
            }}
          />
        </FormField>
      )}
      {contentEditor === HtmlEditor.Ace && (
        <FormField isHorizontalLayout labelText={displayContent.labelHtmlEditorTheme}>
          <BasicSelect
            data-test="preferences-html-editor-theme"
            defaultValues={selectedSideBySideTheme ? [selectedSideBySideTheme] : []}
            id="preferences-html-editor-theme"
            onSelect={([newSideBySideTheme]) => {
              setSelectedSideBySideTheme(newSideBySideTheme as AceEditorTheme);
            }}
            Option={BasicOption}
            options={getSideBySideEditorThemeOptions()}
            isFullWidth
          />
        </FormField>
      )}
      <FormField
        descriptionText={
          contentEditor !== HtmlEditor.Bee && wysiwygEditorEnabled
            ? displayContent.strWarningHtmlSwitch
            : ''
        }
        isHorizontalLayout
        labelText={displayContent.labelContentEditor}
      >
        {contentEditor !== HtmlEditor.Bee && wysiwygEditorEnabled ? (
          <BasicSelect
            data-test="preferences-content-editor"
            defaultValues={contentEditor ? [contentEditor] : []}
            id="preferences-content-editor"
            onSelect={([newContentEditor]) => {
              setCurrentMediumFields({
                htmlEditor: newContentEditor,
                html: cleanHtmlMediumData.html || '',
              });
            }}
            Option={BasicOption}
            options={getContentEditorOptions()}
            isFullWidth
          />
        ) : (
          <SettingsReadOnlyText data-test="preferences-content-editor-ro">
            {CONTENT_EDITOR_LABEL_MAPPER[contentEditor as HtmlEditor]}
          </SettingsReadOnlyText>
        )}
      </FormField>
    </StyledModalBody>
  );
};
